import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Flash} from '@primer/react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {BlockType, FilterQuery, type FilterProvider} from '@github-ui/filter-core'
import {escapeString} from '@github-ui/filter-core'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {TimePeriodFilterProvider} from './providers/time-period'
import {EvaluateStatusFilterProvider} from './providers/evaluate-status'
import {StaticBranchFilterProvider} from './providers/static-branch'
import {KeyOnlyBranchFilterProvider} from './providers/key-only-branch'
import {RulesetFilterProvider} from './providers/ruleset'
import {SecurityProductsRepositoryFilterProvider} from './providers/security-products-repository-filter-provider'
import {StaticOrgFilterProvider} from './providers/static-org'
import {Filter, ValidationMessage} from '@github-ui/filter'
import {CreatedFilterProvider} from './providers/date'
import {TimeoutsFilterProvider} from './providers/timeouts'

// --- Types ---

/** Base type for filter objects. Example: {timePeriod: 'day', repository: 'github/github'} */
export type FilterObject = Record<string, unknown>

/** Shape of values from Filter component's query blocks. Examples:
 *  - {value: 'day', displayName: 'Last 24 hours'} */
export type FilterValue = {value?: string | (() => string); displayName?: string}

export type FieldConfig<T, K extends keyof T = keyof T> = {
  filterKey: K
  queryKey: string
  parse?: (values: FilterValue[]) => T[K]
  serialize?: (value: T[K]) => string
  /** Value to use when user explicitly removes this filter from input.
   *  Filters with fallback are also shown first in the input. */
  fallback?: T[K]
}
export const fieldConfig = <T extends FilterObject>(config: FieldConfig<T>): FieldConfig<T> => config

/** Bundles a FieldConfig with its FilterProvider - ensures they stay in sync */
export type FilterDefinition<T> = {
  config: FieldConfig<T>
  provider: FilterProvider
}
export const filterDefinition = <T extends FilterObject>(def: FilterDefinition<T>): FilterDefinition<T> => def

// --- Shared filter definitions (used by multiple filter bars) ---

export const timePeriodFilterDefinition = <T extends FilterObject>(): FilterDefinition<T> => ({
  config: {
    filterKey: 'timePeriod',
    queryKey: 'time-period',
    fallback: 'month',
  } as FieldConfig<T>,
  provider: new TimePeriodFilterProvider(),
})

export const createdFilterDefinition = <T extends FilterObject>(): FilterDefinition<T> => ({
  config: {
    filterKey: 'created',
    queryKey: 'created',
    fallback: '>@today-30d',
    parse: (values: FilterValue[]) => {
      const resolved = values.map(v => resolveValue(v.value)).filter(Boolean)
      // Preserve range syntax (e.g. "2026-03-04..2026-03-05") when two values are present
      return resolved.length > 1 ? resolved.join('..') : resolved[0]
    },
  } as FieldConfig<T>,
  provider: new CreatedFilterProvider(),
})

/** Repository filter for org-level pages */
export const repositoryFilterDefinition = <T extends FilterObject>(
  repoSuggestionsUrl: string,
): FilterDefinition<T> => ({
  config: {filterKey: 'repository', queryKey: 'repo'},
  provider: new SecurityProductsRepositoryFilterProvider(repoSuggestionsUrl),
})

/** Organization filter for enterprise-level pages */
export const staticOrganizationFilterDefinition = <T extends FilterObject>(
  organizations: string[],
): FilterDefinition<T> => ({
  config: {filterKey: 'organization', queryKey: 'org'},
  // Uses pre-loaded orgs because the generic org suggestions endpoint requires enterprise owner access
  provider: new StaticOrgFilterProvider(organizations),
})

/** Evaluate status filter (active / evaluate / all) */
export const evaluateStatusFilterDefinition = <T extends FilterObject>(): FilterDefinition<T> => ({
  config: {
    filterKey: 'evaluateStatus',
    queryKey: 'evaluate-status',
    fallback: 'all',
  } as FieldConfig<T>,
  provider: new EvaluateStatusFilterProvider(),
})

/** Timeout status filter (exclude / include / only) */
export const timeoutsFilterDefinition = <T extends FilterObject>(): FilterDefinition<T> => ({
  config: {
    filterKey: 'timeouts',
    queryKey: 'timeouts',
    fallback: 'include',
  } as FieldConfig<T>,
  provider: new TimeoutsFilterProvider(),
})

/** Branch filter for repo-level pages using a static list of branches */
export const branchFilterDefinition = <T extends FilterObject>(branches: string[]): FilterDefinition<T> => ({
  config: {filterKey: 'branch', queryKey: 'branch'},
  provider: new StaticBranchFilterProvider(branches),
})

/** Branch filter that accepts typed input but offers no suggestions */
export const keyOnlyBranchFilterDefinition = <T extends FilterObject>(): FilterDefinition<T> => ({
  config: {filterKey: 'branch', queryKey: 'branch'},
  provider: new KeyOnlyBranchFilterProvider(),
})

/** Ruleset filter using a list of rulesets (matches by name) */
export const rulesetFilterDefinition = <T extends FilterObject>(
  rulesets: Array<{name: string}>,
): FilterDefinition<T> => ({
  config: {
    filterKey: 'ruleset',
    queryKey: 'ruleset',
    parse: (values: FilterValue[]) => rulesets.find(r => r.name === values[0]?.displayName),
    serialize: (v: unknown) => serializeName(v as {name: string}),
  } as FieldConfig<T>,
  provider: new RulesetFilterProvider(rulesets),
})

// --- Parsing: filter string -> filter object ---

const resolveValue = (val: FilterValue['value']): string | undefined => (typeof val === 'function' ? val() : val)
const parseFirstValue = (values: FilterValue[]): string | undefined => resolveValue(values[0]?.value)

/** Parses filter value as a user object. Only login is meaningful;
 *  other fields satisfy type requirements which can be removed once old filter code is removed. */
export const parseAsUser = (values: FilterValue[]) => {
  const login = resolveValue(values[0]?.value)
  if (!login) return undefined
  return {login, name: '', path: '', primaryAvatarUrl: ''}
}

/** User submits filter input -> filter object for URL/server
 *  e.g. "time-period:day repo:github" -> {timePeriod: 'day', repository: 'github'} */
const parseInputToFilter = <T extends FilterObject>(query: FilterQuery, fieldConfigs: Array<FieldConfig<T>>): T => {
  const filter = {} as T
  const filterBlocks = query.blocks.filter(b => b.type === BlockType.Filter)

  for (const block of filterBlocks) {
    const queryKey = block.provider?.key
    const values = block.value?.values
    if (!queryKey || !values?.length) continue

    const config = fieldConfigs.find(c => c.queryKey === queryKey)
    if (!config) continue

    const parse = config.parse ?? parseFirstValue
    const parsedValue = parse(values)
    if (parsedValue !== undefined) {
      ;(filter as FilterObject)[config.filterKey as string] = parsedValue
    }
  }

  return filter
}

// --- Serialization: filter object -> filter string ---

export const serializeLogin = <T extends {login: string}>(v: T) => v.login
export const serializeName = <T extends {name: string}>(v: T) => v.name

/** Filter object -> string for UI display. Excludes auto-applied fallback values so input
 *  appears empty when user clears it, while fallbacks are still applied by the backend. */
const serializeFilterForDisplay = <T extends FilterObject>(
  filter: T,
  fieldConfigs: Array<FieldConfig<T>>,
  autoAppliedFallbackKeys: Set<string>,
): string =>
  fieldConfigs
    .map(({filterKey, queryKey, serialize = String}) => {
      const value = filter[filterKey]
      // Skip if value is null/undefined OR this key was auto-applied as fallback
      if (value == null || autoAppliedFallbackKeys.has(queryKey)) return null
      const stringValue = serialize(value)
      return stringValue ? `${queryKey}:${escapeString(stringValue)}` : null
    })
    .filter(Boolean)
    .join(' ')

// --- Fallback handling ---
// Fallback values are used when user explicitly removes a filter from input.
// e.g., deleting time-period means "show all time", so we fall back to "month" (our max).
// Filters with fallback are also shown first in the input.

const extractFallbackValues = <T extends FilterObject>(fieldConfigs: Array<FieldConfig<T>>): Partial<T> => {
  const result = {} as Partial<T>
  for (const config of fieldConfigs) {
    if (config.fallback !== undefined) {
      ;(result as FilterObject)[config.filterKey as string] = config.fallback
    }
  }
  return result
}

const applyFallbackValues = <T extends FilterObject>(filter: T, fallbacks: Partial<T>): T => {
  if (Object.keys(fallbacks).length === 0) return filter
  return {...fallbacks, ...filter}
}

// --- Key ordering: preserves the order user typed filters in ---

const sortConfigsByKeyOrder = <T extends FilterObject>(
  fieldConfigs: Array<FieldConfig<T>>,
  keyOrder: string[],
): Array<FieldConfig<T>> =>
  [...fieldConfigs].sort((a, b) => {
    const aIndex = keyOrder.indexOf(a.queryKey)
    const bIndex = keyOrder.indexOf(b.queryKey)
    if (aIndex === -1 && bIndex === -1) return 0
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })

const extractKeyOrderFromQuery = (query: FilterQuery): string[] =>
  query.blocks
    .filter(b => b.type === BlockType.Filter)
    .map(b => b.provider?.key)
    .filter((k): k is string => !!k)

const buildKeyOrderWithFallbacksFirst = (userKeyOrder: string[], fallbackKeys: string[]): string[] => [
  ...fallbackKeys,
  ...userKeyOrder.filter(k => !fallbackKeys.includes(k)),
]

// --- Validation ---

/** Extracts stray text errors (text blocks that aren't valid filters) */
const extractStrayTextErrors = (query: FilterQuery): string[] =>
  query.blocks
    .filter(b => b.type === BlockType.Text)
    .map(b => `Text will be ignored, use one of the predefined filters instead: <pre>${b.raw}</pre>`)

// --- Focus management ---

const useFocusInputOnFilterChange = (inputRef: React.RefObject<HTMLInputElement | null>, filter: unknown) => {
  const isFirstRenderRef = useRef(true)

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }

    const timeoutId = setTimeout(() => {
      const input = inputRef.current
      if (input) {
        input.focus()
        input.setSelectionRange(input.value.length, input.value.length)
      }
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [filter, inputRef])
}

// --- Component ---

type SecurityProductsFilterBarProps<T extends FilterObject & {page?: number}> = {
  id: string
  label: string
  filter: T
  filterDefinitions: Array<FilterDefinition<T>>
  getIndexPath: (options: {filter: T}) => string
}

/** Reusable filter bar for security products (Rules Insights, Bypass Requests, etc.). */
export const SecurityProductsFilterBar = <T extends FilterObject & {page?: number}>({
  id,
  label,
  filter,
  filterDefinitions,
  getIndexPath,
}: SecurityProductsFilterBarProps<T>) => {
  const {navigate} = useRelativeNavigation()
  const fieldConfigs = useMemo(() => filterDefinitions.map(d => d.config), [filterDefinitions])
  const filterProviders = useMemo(() => filterDefinitions.map(d => d.provider), [filterDefinitions])
  const fallbackValues = useMemo(() => extractFallbackValues(fieldConfigs), [fieldConfigs])
  const fallbackKeys = useMemo(
    () => fieldConfigs.filter(c => c.fallback !== undefined).map(c => c.queryKey),
    [fieldConfigs],
  )
  const [keyOrder, setKeyOrder] = useState<string[]>([])
  // Track which fallback keys were auto-applied (not explicitly typed by user)
  // On initial load, assume all fallbacks in the filter were explicitly set (e.g. from URL)
  const [autoAppliedFallbackKeys, setAutoAppliedFallbackKeys] = useState<Set<string>>(() => new Set())

  // Convert filter object to string for display in input (excludes auto-applied fallback values)
  const serializedFilter = useMemo(() => {
    const orderedKeys = buildKeyOrderWithFallbacksFirst(keyOrder, fallbackKeys)
    const sortedConfigs = sortConfigsByKeyOrder(fieldConfigs, orderedKeys)
    return serializeFilterForDisplay(filter, sortedConfigs, autoAppliedFallbackKeys)
  }, [filter, fieldConfigs, keyOrder, fallbackKeys, autoAppliedFallbackKeys])
  // Sync inputValue when the filter prop changes (e.g. browser back/forward, or after handleSubmit
  // triggers a navigation). We compare filter to prevFilter instead of using useEffect so the update
  // happens during render — before paint — avoiding a visible flash of stale input text.
  // See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevFilter, setPrevFilter] = useState(filter)
  const [inputValue, setInputValue] = useState(serializedFilter)
  if (filter !== prevFilter) {
    setPrevFilter(filter)
    setInputValue(serializedFilter)
    // Clear auto-applied fallbacks so serializedFilter shows all filter values. This is essential
    // for external navigations (browser back/forward) where handleSubmit never ran. After an
    // internal submit it's redundant since handleSubmit already set inputValue correctly.
    setAutoAppliedFallbackKeys(new Set())
  }

  // Validation errors from the Filter's onValidation callback (excludes stray text errors)
  const [filterValidationErrors, setFilterValidationErrors] = useState<string[]>([])
  // Stray text errors shown only on submit
  const [strayTextErrors, setStrayTextErrors] = useState<string[]>([])
  // Combined validation errors for display
  const validationErrors = useMemo(
    () => [...filterValidationErrors, ...strayTextErrors],
    [filterValidationErrors, strayTextErrors],
  )
  // Use a unique id (different from Filter's internal id) to avoid duplicate IDs
  const validationMessageId = `${id}-filter-validation`

  // Called by Filter's onValidation prop - validates everything except stray text
  const handleValidation = useCallback((messages: string[]) => {
    setFilterValidationErrors(messages)
  }, [])

  // Clear stray text errors when user modifies the filter (will be re-checked on submit)
  const handleChange = useCallback((value: string) => {
    setInputValue(value)
    setStrayTextErrors([])
  }, [])

  const handleSubmit = useCallback(
    (query: FilterQuery) => {
      // Check for stray text errors on submit
      const textErrors = extractStrayTextErrors(query)
      setStrayTextErrors(textErrors)

      // If there are stray text errors, don't navigate
      if (textErrors.length > 0) {
        return
      }

      // Preserve the order user typed, but put fallback fields first
      const userKeyOrder = extractKeyOrderFromQuery(query)
      const keyOrderWithFallbacksFirst = buildKeyOrderWithFallbacksFirst(userKeyOrder, fallbackKeys)
      setKeyOrder(keyOrderWithFallbacksFirst)

      // Determine which fallback keys were auto-applied (not typed by user)
      const newAutoAppliedFallbackKeys = new Set(fallbackKeys.filter(k => !userKeyOrder.includes(k)))
      setAutoAppliedFallbackKeys(newAutoAppliedFallbackKeys)

      // Parse input, apply fallbacks, update display, and navigate
      const parsedFilter = parseInputToFilter(query, fieldConfigs)
      const filterWithFallbacks = applyFallbackValues({...parsedFilter, page: undefined}, fallbackValues)

      const sortedConfigs = sortConfigsByKeyOrder(fieldConfigs, keyOrderWithFallbacksFirst)
      // Display excludes auto-applied fallback values so input appears empty when cleared
      setInputValue(serializeFilterForDisplay(filterWithFallbacks, sortedConfigs, newAutoAppliedFallbackKeys))

      navigate('.', getIndexPath({filter: filterWithFallbacks}), true)
    },
    [navigate, fieldConfigs, getIndexPath, fallbackValues, fallbackKeys],
  )

  const inputRef = useRef<HTMLInputElement | null>(null)
  useFocusInputOnFilterChange(inputRef, filter)
  return (
    <ErrorBoundary
      fallback={<Flash variant="warning">An error occurred loading the filter. Please refresh the page.</Flash>}
    >
      <div>
        <Filter
          id={id}
          label={label}
          onSubmit={handleSubmit}
          onValidation={handleValidation}
          providers={filterProviders}
          filterValue={inputValue}
          onChange={handleChange}
          variant="input"
          inputRef={inputRef}
          showValidationMessage={false}
        />
        {validationErrors.length > 0 && (
          <div className="mt-2" role="alert" aria-live="polite">
            <ValidationMessage messages={validationErrors} id={validationMessageId} />
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}
